/**
 * report-constants.js
 */

import { Types } from 'utils/constants';
import analytics from 'utils/analytics';



export const detailReportLimit = 5000;
export const reportViewCutoffMinutes = 2;


export const ReportSourceType = {
  SINGLE: 'record-report-single',
  MULTIPLE: 'record-report-multiple',
};


export const ReportRunType = {
  VIEW: 'view',
  DOWNLOAD: 'download',
};


export const RecordSetButton = {
  STANDARD: 'standard',
  CUSTOM: 'custom'
};


export const RecordSetType = {
  ALL: 'all',
  SEARCHED: 'searched',
  SELECTED: 'selected',
};


export const DateRange = {
  ALL: 'all',
  THIS_MONTH: 'this-month',
  LAST_MONTH: 'last-month',
  LAST_30: 'last-30',
  LAST_90: 'last-90',
  THIS_YEAR: 'this-year',
  LAST_YEAR: 'last-year',
  CUSTOM_RANGE: 'custom-range',
};


export const DefaultDateRange = {
  id: DateRange.ALL,
  start: null,
  end: null
};


export const ReportType = {
  TRAINING_SUMMARY: 'training-summary',
  EXERCISE_DETAIL: 'exercise-detail',
  EXERCISE_LOG: 'exercise-log',
  EXERCISE_ODORS: 'exercise-odors',
  DEPLOYMENT_DETAIL: 'deployment-detail',
  DEPLOYMENT_SUMMARY: 'deployment-summary',
  DEPLOYMENT_LOG: 'deployment-log',
  CLASS_DETAIL: 'class-detail',
  VET_VISIT_DETAIL: 'vet-visit-detail',
  VACCINATION_SUMMARY: 'vaccination-summary',
};


export const ReportTypeLabel = {
  [ReportType.TRAINING_SUMMARY]: 'Training Summary',
  [ReportType.EXERCISE_DETAIL]: 'Exercise',
  [ReportType.EXERCISE_LOG]: 'Exercise Log',
  [ReportType.EXERCISE_ODORS]: 'Exercise Odor List',
  [ReportType.DEPLOYMENT_DETAIL]: 'Deployment',
  [ReportType.DEPLOYMENT_SUMMARY]: 'Deployment Summary',
  [ReportType.DEPLOYMENT_LOG]: 'Deployment Log',
  [ReportType.CLASS_DETAIL]: 'Class',
  [ReportType.VET_VISIT_DETAIL]: 'Vet Visit',
  [ReportType.VACCINATION_SUMMARY]: 'Vaccination Summary',
};


export const ReportTypeOptionType = {
  HEADER: 'header',
  OPTION: 'option',
};


export const ReportTypeOptions = {
  [Types.RecordGroup.TRAINING]: [{
      type: ReportTypeOptionType.HEADER,
      label: 'Training'
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.TRAINING_SUMMARY,
      label: ReportTypeLabel[ReportType.TRAINING_SUMMARY],
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.EXERCISE_DETAIL,
      label: ReportTypeLabel[ReportType.EXERCISE_DETAIL],
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.EXERCISE_LOG,
      label: ReportTypeLabel[ReportType.EXERCISE_LOG],
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.EXERCISE_ODORS,
      label: ReportTypeLabel[ReportType.EXERCISE_ODORS],
    }],
  [Types.RecordGroup.DEPLOYMENT]: [{
      type: ReportTypeOptionType.HEADER,
      label: 'Deployment'
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.DEPLOYMENT_DETAIL,
      label: ReportTypeLabel[ReportType.DEPLOYMENT_DETAIL],
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.DEPLOYMENT_SUMMARY,
      label: ReportTypeLabel[ReportType.DEPLOYMENT_SUMMARY],
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.DEPLOYMENT_LOG,
      label: ReportTypeLabel[ReportType.DEPLOYMENT_LOG],
    }],
  [Types.RecordGroup.CLASS]: [{
      type: ReportTypeOptionType.HEADER,
      label: 'Class'
    }, {
      type: ReportTypeOptionType.OPTION,
      value: ReportType.CLASS_DETAIL,
      label: ReportTypeLabel[ReportType.CLASS_DETAIL],
    }],
  [Types.RecordGroup.VET_VISIT]: [{
    type: ReportTypeOptionType.HEADER,
    label: 'Vet Visit'
  }, {
    type: ReportTypeOptionType.OPTION,
    value: ReportType.VET_VISIT_DETAIL,
    label: ReportTypeLabel[ReportType.VET_VISIT_DETAIL],
  }, {
    type: ReportTypeOptionType.OPTION,
    value: ReportType.VACCINATION_SUMMARY,
    label: ReportTypeLabel[ReportType.VACCINATION_SUMMARY],
  }],
};


export const ReportTypeInfo = {
  [ReportType.TRAINING_SUMMARY]: 'Exercise and Class data will be combined into a graphical summary of key statistics',
  [ReportType.EXERCISE_DETAIL]: 'Each exercise record will be printed in its entirety including all images',
  [ReportType.EXERCISE_LOG]: 'Exercise records will be displayed in a list and grouped by completion status',
  [ReportType.EXERCISE_ODORS]: 'Each trained odor will be listed in a wide table with associated detection data',
  [ReportType.DEPLOYMENT_DETAIL]: 'Each deployment record will be printed in its entirety including all images',
  [ReportType.DEPLOYMENT_SUMMARY]: 'Deployment data will be combined into a graphical summary of key statistics',
  [ReportType.DEPLOYMENT_LOG]: 'Deployment records will be displayed in a list and grouped by completion status',
  [ReportType.CLASS_DETAIL]: 'Each classroom record will be printed in its entirety',
  [ReportType.VET_VISIT_DETAIL]: 'Each vet visit record will be printed in its entirety',
  [ReportType.VACCINATION_SUMMARY]: 'Immunization information will be summarized by vaccine and date',
};


export const reportAnalytics = (
  reportSourceType,
  reportRunType,
  recordSetType,
  reportType,
  numRecords,
  t
) => {

  // determines record group from the report type
  const getRecordGroup = (reportType) => {
    // TRAINING (we don't distinguish between dog/handler training here because apparently we don't actually care)
    if ([
      ReportType.TRAINING_SUMMARY,
      ReportType.EXERCISE_DETAIL,
      ReportType.EXERCISE_LOG,
      ReportType.EXERCISE_ODORS
    ].includes(reportType)) {
      return Types.RecordGroup.TRAINING;
    } 
    // DEPLOYMENT
    else if ([
      ReportType.DEPLOYMENT_DETAIL,
      ReportType.DEPLOYMENT_SUMMARY,
      ReportType.DEPLOYMENT_LOG
    ].includes(reportType)) {
      return Types.RecordGroup.DEPLOYMENT;
    } 
    // CLASS
    else if ([
      ReportType.CLASS_DETAIL
    ].includes(reportType)) {
      return [Types.RecordGroup.CLASS];
    }
    // VET VISIT
    else if ([
      ReportType.VACCINATION_SUMMARY,
      ReportType.VET_VISIT_DETAIL
    ].includes(reportType)) {
      return [Types.RecordGroup.VET_VISIT];
    }
  }

  const getReportSizeType = (numRecords) => {
    if (numRecords > 0 && numRecords <= 10) {
      return '1-10';
    } else if (numRecords > 10 && numRecords <= 50) {
      return '11-50';
    } else if (numRecords > 50 && numRecords <= 100) {
      return '51-100';
    } else if (numRecords > 100 && numRecords <= 300) {
      return '101-300';
    } else if (numRecords > 300 && numRecords <= 1000) {
      return '301-1k';
    } else if (numRecords > 1000 && numRecords <= 5000) {
      return '1k-5k';
    } else if (numRecords > 5000 && numRecords <= 10000) {
      return '5K-10k';
    } else if (numRecords > 10000 && numRecords <= 20000) {
      return '10k-20k';
    } else {
      return '20k+';
    }
  }


  const recordGroupName = Types.RecordGroup.getName(t, getRecordGroup(reportType)).toLowerCase();
  const reportSizeType = getReportSizeType(numRecords);


  // GA4
  analytics.trackGA4('event', {
    name: reportSourceType.replace(/-/g, '_'), 
    params: reportSourceType === ReportSourceType.SINGLE
      ? { 
        record_group_type: recordGroupName, 
        report_type: reportType.replace(/-/g, '_'),
      } 
      : {
        record_group_type: recordGroupName, 
        record_set_type: recordSetType,
        report_run_type: reportRunType,
        report_type: reportType.replace(/-/g, '_'),
        report_size_type: reportSizeType,
      }
  });
}